'use client';

import { useState } from 'react';
import type { CheckoutInput } from '@/src/lib/hospitality/orderWire';
import { Field } from './Fulfilment';

/**
 * Contact details, notes and terms — everything the placed order needs that the
 * cart does not carry.
 *
 * <p>`requestedDeliveryAt` is passed in rather than collected here: `Cart` has
 * no column for it, so the chosen instant lives in this form's parent and is
 * sent to `checkout()`. Writing it onto the cart is not an option that exists.
 *
 * <p>Accepting the terms is what populates `termsDocumentKey`,
 * `termsDocumentVersion`, `termsAcceptedAt` and `acceptedFromIp` on the order.
 * Only the first two are sent; the timestamp and the IP are stamped by the
 * backend from the request itself, because an acceptance time a client chose is
 * worth nothing as evidence. The checkbox is therefore only offered when the
 * operator has configured a terms document — an unticked box that stamps
 * nothing is theatre.
 */

export interface CheckoutFormProps {
  t: (key: string, params?: Record<string, string | number>) => string;
  busy: boolean;
  termsDocumentKey?: string;
  termsDocumentVersion?: string;
  termsHref?: string;
  requestedAt: string | null;
  requestedAtLabel: string | null;
  error: string | null;
  onBack: () => void;
  onSubmit: (input: CheckoutInput) => void;
}

export function CheckoutForm({
  t,
  busy,
  termsDocumentKey,
  termsDocumentVersion,
  termsHref,
  requestedAt,
  requestedAtLabel,
  error,
  onBack,
  onSubmit,
}: CheckoutFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [accepted, setAccepted] = useState(false);

  const termsRequired = !!termsDocumentKey;
  const ready =
    name.trim() !== '' &&
    /.+@.+\..+/.test(email.trim()) &&
    (!termsRequired || accepted);

  return (
    <form
      className="mt-4 border-t pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || busy) return;
        onSubmit({
          contactName: name.trim(),
          contactEmail: email.trim(),
          contactPhone: phone.trim() || null,
          note: note.trim() || null,
          requestedDeliveryAt: requestedAt,
          termsDocumentKey: termsRequired && accepted ? termsDocumentKey : null,
          termsDocumentVersion: termsRequired && accepted ? (termsDocumentVersion ?? null) : null,
        });
      }}
    >
      <h3 className="text-lg font-semibold">{t('hospitality.order.checkout')}</h3>

      {requestedAtLabel ? (
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('hospitality.order.requestedFor', { at: requestedAtLabel })}
        </p>
      ) : null}

      <Field id="checkout-name" label={t('hospitality.order.contactName')} value={name} onChange={setName} required />
      <Field id="checkout-email" label={t('hospitality.order.contactEmail')} value={email} onChange={setEmail} required type="email" />
      <Field id="checkout-phone" label={t('hospitality.order.contactPhone')} value={phone} onChange={setPhone} type="tel" />

      <div className="mt-2">
        <label htmlFor="checkout-note" className="block text-sm font-medium">
          {t('hospitality.order.note')}
        </label>
        <textarea
          id="checkout-note"
          value={note}
          rows={3}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          className="mt-1 w-full rounded border px-2 py-1 text-sm"
        />
      </div>

      {termsRequired ? (
        <div className="mt-3 flex items-start gap-2">
          <input
            id="checkout-terms"
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            className="mt-1"
          />
          <label htmlFor="checkout-terms" className="text-sm">
            {t('hospitality.order.acceptTerms')}
            {termsHref ? (
              <>
                {' '}
                <a className="underline" href={termsHref} target="_blank" rel="noreferrer">
                  {t('hospitality.order.termsLink')}
                </a>
              </>
            ) : null}
          </label>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={onBack} className="rounded border px-4 py-2 text-sm">
          {t('hospitality.order.back')}
        </button>
        <button
          type="submit"
          disabled={!ready || busy}
          className="rounded bg-black px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {t('hospitality.order.placeOrder')}
        </button>
      </div>
    </form>
  );
}
